import type { ReactElement } from 'react';
import type { EmployerDayTimeBucket } from '../../../utils/employerTaskDayGrouping';

type EmployerTimeBucketIconProps = {
  bucket: EmployerDayTimeBucket;
  className?: string;
};

const MorningIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <defs>
      <linearGradient id="employerTimeBucket-sunriseSky" x1="15" y1="0" x2="15" y2="30" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#F97316" />
        <stop offset="60%" stopColor="#FDBA74" />
        <stop offset="100%" stopColor="#FEF08A" />
      </linearGradient>
    </defs>
    <rect width="30" height="30" rx="6" fill="#FFEDD5" />
    <rect x="0" y="0" width="30" height="30" fill="url(#employerTimeBucket-sunriseSky)" />
    <circle cx="21" cy="18" r="5" fill="#FEF08C" stroke="#A16207" strokeWidth="0.3" />
    <g stroke="#FEF08C" strokeWidth="1.8" strokeLinecap="round">
      <line x1="21" y1="11" x2="21" y2="9" />
      <line x1="14" y1="18" x2="12" y2="18" />
      <line x1="16" y1="13" x2="14" y2="11" />
      <line x1="26" y1="13" x2="28" y2="11" />
      <line x1="21" y1="25" x2="21" y2="27" />
    </g>
    <path d="M0 30 L0 23 L8 19 L16 22 L24 17 L30 21 L30 30 Z" fill="#14532D" />
  </svg>
);

export const AfternoonIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect width="30" height="30" rx="6" fill="#E0F2FE" />
    <rect x="0" y="0" width="30" height="30" fill="#7DD3FC" />
    <circle cx="15" cy="12" r="6" fill="#FEF08C" stroke="#A16207" strokeWidth="0.3" />
    <g stroke="#FEF08C" strokeWidth="2.2" strokeLinecap="round">
      <line x1="15" y1="4" x2="15" y2="1.5" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="5" y1="12" x2="2.5" y2="12" />
      <line x1="25" y1="12" x2="27.5" y2="12" />
      <line x1="8" y1="6.5" x2="5.5" y2="4.5" />
      <line x1="22" y1="6.5" x2="24.5" y2="4.5" />
      <line x1="8" y1="17.5" x2="5.5" y2="19.5" />
      <line x1="22" y1="17.5" x2="24.5" y2="19.5" />
    </g>
    <ellipse cx="8" cy="24" rx="4.2" ry="2.8" fill="#F8FAFC" opacity="0.95" />
    <ellipse cx="11.5" cy="23" rx="3.5" ry="2.6" fill="#F8FAFC" opacity="0.95" />
    <ellipse cx="15" cy="24.5" rx="3" ry="2.4" fill="#F8FAFC" opacity="0.95" />
    <ellipse cx="23" cy="28" rx="3.8" ry="2.5" fill="#F8FAFC" opacity="0.9" />
    <ellipse cx="20" cy="27" rx="2.6" ry="2.3" fill="#F8FAFC" opacity="0.9" />
  </svg>
);

export const LateNightIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect width="30" height="30" fill="#0F172A" />
    <rect x="0" y="19" width="30" height="11" fill="#1E2937" />
    <path d="M0 24 L6 19 L12 23 L18 17 L24 22 L30 19 L30 30 L0 30 Z" fill="#334155" />
    <circle cx="9" cy="9" r="3.8" fill="#E2E8F0" />
    <circle cx="22" cy="6" r="0.7" fill="#F1F5F9" />
    <circle cx="26" cy="13" r="0.6" fill="#F1F5F9" />
    <circle cx="5" cy="14" r="0.8" fill="#F1F5F9" />
    <circle cx="18" cy="16" r="0.5" fill="#F1F5F9" />
    <circle cx="28" cy="20" r="0.6" fill="#F1F5F9" />
  </svg>
);

export const EveningIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect width="30" height="30" fill="#1E2937" />
    <rect x="0" y="19" width="30" height="11" fill="#334155" />
    <path d="M0 24 L6 19 L12 23 L18 17 L24 22 L30 19 L30 30 L0 30 Z" fill="#475569" />
    <circle cx="19" cy="11" r="5.5" fill="#FEF08A" />
    <circle cx="21" cy="10" r="4.5" fill="#1E2937" />
    <circle cx="7" cy="7" r="0.8" fill="#F1F5F9" />
    <circle cx="25" cy="16" r="0.7" fill="#F1F5F9" />
  </svg>
);

const NoTimeIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect width="30" height="30" rx="6" fill="#64748B" />
    <circle cx="15" cy="15" r="10" stroke="#E2E8F0" strokeWidth="2.5" fill="none" />
    <circle cx="15" cy="15" r="1.5" fill="#E2E8F0" />
    <line x1="9" y1="9" x2="21" y2="21" stroke="#E2E8F0" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const BUCKET_ICON: Record<EmployerDayTimeBucket, () => ReactElement> = {
  morning: MorningIcon,
  afternoon: AfternoonIcon,
  lateNight: LateNightIcon,
  evening: EveningIcon,
  noTime: NoTimeIcon,
};

export const EmployerTimeBucketIcon = ({ bucket, className = 'h-[30px] w-[30px] shrink-0' }: EmployerTimeBucketIconProps) => {
  const Icon = BUCKET_ICON[bucket];
  return (
    <span className={className}>
      <Icon />
    </span>
  );
};
